import Particles from '@tsparticles/react';
import Button, { CountdownButton, PrimaryButton, TertiaryButton } from 'components/Button/src/jsx/Button';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useCustomization } from '../hooks/useCustomization';
import { useParticles } from '../hooks/useParticles';
import { useScene } from '../hooks/useScene';
import CustomizationVisual from './CustomizationVisual';
import Heading from './Heading';
import Input from './ui/Input';
import Radio from './ui/Radio';
import Select from './ui/Select';

function SelectionControls({ step, customizationOptions, selection, onChangeOption, getLabel }) {
	const mappedOptions = useMemo(() => customizationOptions.map(({ id, name }) => ({ value: id, label: name })), [customizationOptions]);

	return (
		<div className="selection__controls">
			<Select
				label={getLabel(step === 0 ? 'customization_select_goal_label' : 'customization_select_role_label')}
				onChange={onChangeOption}
				options={mappedOptions}
				testId="trial-option-select"
				value={selection[step].value}
			/>
			{customizationOptions.map((option) => (
				<Radio
					key={`option-${step}-${option.id}`}
					name={step.toString()}
					id={option.id}
					value={option.id}
					checked={option.id === selection[step].value}
					callback={onChangeOption}
					label={option.name}
					testId="trial-option-radio"
				/>
			))}
		</div>
	);
}

SelectionControls.propTypes = {
	step: PropTypes.number.isRequired,
	customizationOptions: PropTypes.array.isRequired,
	selection: PropTypes.array.isRequired,
	onChangeOption: PropTypes.func.isRequired,
	getLabel: PropTypes.func.isRequired,
};

export default function Customization({ data, getLabel, onCompleteCustomization, setSceneData }) {
	const {
		customizationOptions,
		finishCustomization,
		goToFirstStep,
		goToNextStep,
		onChangeOption,
		onCustomInputChange,
		selection,
		skipCustomization,
		step,
		totalSteps,
	} = useCustomization({
		data: data.customization,
		onCompleteCustomization,
	});

	const { showParticles, particleOptions, particlesLoaded } = useParticles(step);
	useScene(step < 2 ? 'customization' : 'completion', { data, setSceneData });

	// Map step to heading content for readability
	const headingMap = [
		{ title: getLabel('customization_heading_goal_selection'), subtitle: getLabel('customization_subtitle_goal_selection') },
		{ title: getLabel('customization_heading_role_selection'), subtitle: getLabel('customization_subtitle_role_selection') },
		{ title: getLabel('customization_heading_completion'), subtitle: '' },
	];

	return (
		<>
			<Heading title={headingMap[step].title} subtitle={headingMap[step].subtitle} />
			<div className="trial__customization">
				{step < 2 ? (
					<>
						<div className="customization__selection">
							<SelectionControls
								step={step}
								customizationOptions={customizationOptions}
								selection={selection}
								onChangeOption={onChangeOption}
								getLabel={getLabel}
							/>
							<div className="selection__visual">
								<CustomizationVisual data={data.customization} selection={selection} step={step} />
							</div>
						</div>
						{selection[step].value === 'custom' && (
							<Input
								name="custom"
								label={getLabel(step === 0 ? 'customization_custom_goal_input_label' : 'customization_custom_role_input_label')}
								onChange={onCustomInputChange}
								value={selection[step].label}
							/>
						)}
					</>
				) : (
					<div className="customization__completion">
						{showParticles && <Particles id="tsparticles" options={particleOptions} particlesLoaded={particlesLoaded} />}
						<img src={data.visuals.completion.full_url} alt={data.visuals.completion.alt} srcSet={data.visuals.completion.srcset} />
					</div>
				)}
				<div className="customization__navigation">
					{step > 0 && (
						<Button id="customization-back" testId="nav-back" onClick={goToFirstStep}>
							{getLabel('nav_btn_back')}
						</Button>
					)}
					{step < totalSteps - 1 && (
						<>
							<TertiaryButton id="customization-skip" testId="nav-skip" onClick={skipCustomization}>
								{getLabel('nav_btn_skip')}
							</TertiaryButton>
							<PrimaryButton id="customization-next" testId="nav-continue" onClick={goToNextStep}>
								{getLabel('nav_btn_continue')}
							</PrimaryButton>
						</>
					)}
					{step === totalSteps - 1 && (
						<CountdownButton id="customization-finish" testId="nav-continue" onClick={finishCustomization} duration={10}>
							{getLabel('nav_btn_start')}
						</CountdownButton>
					)}
				</div>
			</div>
		</>
	);
}

Customization.propTypes = {
	data: PropTypes.object.isRequired,
	getLabel: PropTypes.func.isRequired,
	onCompleteCustomization: PropTypes.func.isRequired,
	setSceneData: PropTypes.func.isRequired,
};
